/**
 * Громкость на пульте: отдельно гудки и отдельно голос клиента (14.09.2026,
 * просьба диспетчеров — «по ушам бьёт»).
 *
 * Гудок и голос приходят одним звуковым потоком со станции в один элемент
 * `#cc-softphone-audio`, поэтому громкость переключается по фазе звонка:
 * до ответа абонента — «гудки» (и сообщения оператора), после ответа — «голос».
 *
 * Громкость ставится на самом элементе, а не через Web Audio: так не ломается
 * выбор наушников (`setSinkId` работает на элементе). Цена — потолок 100%:
 * элемент умеет только приглушать.
 *
 * Выбор хранится в браузере диспетчера и переживает F5 и следующую смену.
 */

export type CallVolume = {
  /** Гудки и всё, что играет до ответа абонента, % */
  ring: number;
  /** Голос клиента после ответа, % */
  talk: number;
};

const STORAGE_KEY = 'cc.desk.volume';
const AUDIO_ELEMENT_ID = 'cc-softphone-audio';

export const DEFAULT_VOLUME: CallVolume = { ring: 60, talk: 100 };

function clampPercent(value: unknown, fallback: number): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return fallback;
  return Math.min(100, Math.max(0, Math.round(value)));
}

export function readVolume(): CallVolume {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === null) return DEFAULT_VOLUME;
    const parsed = JSON.parse(raw) as Partial<CallVolume>;
    return {
      ring: clampPercent(parsed.ring, DEFAULT_VOLUME.ring),
      talk: clampPercent(parsed.talk, DEFAULT_VOLUME.talk),
    };
  } catch {
    return DEFAULT_VOLUME;
  }
}

export function writeVolume(volume: CallVolume): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(volume));
  } catch {
    // хранилище недоступно — громкость просто не переживёт перезагрузку
  }
}

/**
 * Какая громкость сейчас: пока абонент не ответил — гудки, после ответа или
 * без звонка — голос. Без звонка ставим голос, чтобы следующий карусельный
 * звонок (абонент уже на линии) не начался с приглушённого голоса.
 */
export function volumeForPhase(
  phase: { hasCall: boolean; answered: boolean },
  volume: CallVolume,
): number {
  const percent = phase.hasCall && !phase.answered ? volume.ring : volume.talk;
  return clampPercent(percent, 100) / 100;
}

export function applyVolume(level: number): void {
  if (typeof document === 'undefined') return;
  const element = document.getElementById(AUDIO_ELEMENT_ID);
  if (element instanceof HTMLAudioElement) {
    element.volume = Math.min(1, Math.max(0, level));
  }
}
